import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { BrokerCard } from "@/components/common/BrokerCard";
import { CTASection } from "@/components/common/CTASection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";

const ALL = "all";

export default function BrokerPartners() {
  const { L } = useLang();
  const P = L.pages.brokerPartners;
  usePageSeo("brokerPartners");
  const { data: brokers = [] } = useQuery({ queryKey: ["all-brokers"], queryFn: async () => (await api.get("/brokers")).data });

  const [f, setF] = useState({ market: ALL, platform: ALL, account_type: ALL, jurisdiction: ALL });

  const options = useMemo(() => {
    const collect = (key) => Array.from(new Set(brokers.flatMap((b) => b[key] || []))).sort();
    return {
      market: collect("markets"),
      platform: collect("platforms"),
      account_type: collect("account_types"),
      jurisdiction: collect("regulatory_jurisdictions"),
    };
  }, [brokers]);

  const filtered = brokers.filter((b) =>
    (f.market === ALL || (b.markets || []).includes(f.market)) &&
    (f.platform === ALL || (b.platforms || []).includes(f.platform)) &&
    (f.account_type === ALL || (b.account_types || []).includes(f.account_type)) &&
    (f.jurisdiction === ALL || (b.regulatory_jurisdictions || []).includes(f.jurisdiction))
  );

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF({ market: ALL, platform: ALL, account_type: ALL, jurisdiction: ALL });

  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: P.overline }]}
      />

      <section className="py-16 lg:py-24">
        <div className="container-tight">
          <div className="mb-10 rounded-xl border border-hair bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-ink">
              <SlidersHorizontal className="h-4 w-4 text-finblue" /> {P.filter}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Filter label={P.labels.market} value={f.market} onChange={(v) => set("market", v)} options={options.market} testid="filter-market" allLabel={P.all} />
              <Filter label={P.labels.platform} value={f.platform} onChange={(v) => set("platform", v)} options={options.platform} testid="filter-platform" allLabel={P.all} />
              <Filter label={P.labels.account} value={f.account_type} onChange={(v) => set("account_type", v)} options={options.account_type} testid="filter-account" allLabel={P.all} />
              <Filter label={P.labels.jurisdiction} value={f.jurisdiction} onChange={(v) => set("jurisdiction", v)} options={options.jurisdiction} testid="filter-jurisdiction" allLabel={P.all} />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-subtle">{filtered.length}</p>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="filter-reset">{P.reset}</Button>
            </div>
          </div>

          {filtered.length ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((b) => <BrokerCard key={b.id} broker={b} />)}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-hair bg-canvas py-20 text-center">
              <p className="font-heading text-lg font-semibold text-ink">{P.noResults}</p>
              <p className="mt-1 text-sm text-subtle">{P.noResultsSub}</p>
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}

function Filter({ label, value, onChange, options, testid, allLabel }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wide text-subtle">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{allLabel}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
